//#region IMPORT
import React, { useRef } from 'react';
import dayjs from 'dayjs';
import GridFunctions from '../../../../Services/agGrid/ag-Grid-functions';
import {
  Box as MuiBox,
  Box,
  IconButton,
  Stack,
  Typography,
  Tooltip,
  tooltipClasses,
  styled,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import Icon from '../../../../Assets/Icon/CustomIcons';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { resetAddButtonError } from '../../../../Reducer/StatusFlagsReducer';
// 필터관련 컴포넌트
import { GridFilterButton } from '../../../../Components/GridFilterButton';
import { GridFilterSummary } from '../../../../Components/GridFilterSummary';
import FileUploadDialog from '../../../../Components/FileUploadDialog/FileUploadDialog';
import { getColumnDefs1 } from './HAZ_BASIC_03_ColumnDefs';

/** 우측 툴바 IconButton 공통 크기·원형 (추가/검색/삭제/엑셀 버튼 통일) */
const toolbarIconBtnSx = {
  border: 1,
  borderRadius: '50%',
  width: 40,
  height: 40,
  boxSizing: 'border-box',
  padding: '8px',
};

function HAZ_BASIC_03(props) {
  const dispatch = useDispatch();
  const isAddButtonError = useSelector(
    (state) => state.StatusFlags.isAddButtonError
  );

  // 커스터마이징된 Tooltip 생성
  const LargeTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#4CAF50', // 본체 배경 초록
      color: '#fff',
      fontSize: '0.9rem',
      padding: theme.spacing(1),
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: '#4CAF50', // 꼬리도 동일 색으로!
    },
  }));

  /**
   * 검교정 주기 → 차기 교정일까지 더할 개월 수.
   * UI: 6개월, 1년(12), 2년(24), 직접 입력·기타(자동 계산 없음)
   * DB 과거값: 12m, 18m, 24m, 36m, 기타
   */
  const MeasurementCycleToMonths = (measurementCycle) => {
    const c = String(measurementCycle ?? '').trim();
    if (!c) return null;

    if (c === '직접 입력' || c === '기타') return null;

    if (c === '6개월') return 6;
    if (c === '1년') return 12;
    if (c === '2년') return 24;

    const legacyM = /^(\d+)\s*m$/i.exec(c);
    if (legacyM) {
      const n = parseInt(legacyM[1], 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    }

    return null;
  };

  const CalculateNextCalibrationDate = (calibrationDate, measurementCycle) => {
    if (!calibrationDate || !measurementCycle) return null;

    try {
      const date = new Date(calibrationDate);
      if (isNaN(date.getTime())) return null;

      const months = MeasurementCycleToMonths(measurementCycle);
      if (months == null) return null;

      const nextDate = new Date(date);
      nextDate.setMonth(nextDate.getMonth() + months);
      return nextDate.toISOString();
    } catch (error) {
      console.error('차기 교정일자 계산 오류:', error);
      return null;
    }
  };

  // Grid Options
  const gridOptions = {
    singleClickEdit: true,
    rowClassRules: {
      'new-row': (params) => params.data && params.data.newYn === 'Y',
      'delete-row': (params) => params.data && params.data.deleteYn === 'Y',
      'edit-row': (params) =>
        params.data && params.data.editYn === 'Y' && params.data.newYn !== 'Y',
    },
    isExternalFilterPresent: () => true, // 외부 필터 사용
    doesExternalFilterPass(node) {
      if (node.data?.newYn === 'Y') return true;
      if (node.data?.deleteYn === 'Y') return true;

      // 선택된 필터 값으로 통과 여부 판단
      const filters = selectedFiltersRef.current || {};
      return Object.keys(filters).every((field) => {
        const values = filters[field] || [];
        if (values.length === 0) return true;
        return values.includes(node.data[field]);
      });
    },
  };

  // 상단 영역 ref
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  // 필터관련
  const [gridApi, setGridApi] = React.useState(null);
  const [gridColumnApi, setGridColumnApi] = React.useState(null);
  const [selectedFilters, setSelectedFilters] = React.useState({});
  const [headerMap, setHeaderMap] = React.useState({});
  const [isFiltered, setIsFiltered] = React.useState(false);
  const selectedFiltersRef = React.useRef(selectedFilters);

  React.useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
    if (props.gridRef1?.current?.api) {
      props.gridRef1.current.api.onFilterChanged(); // 필터 적용
    }
  }, [selectedFilters]);

  // 컬럼정보
  const columnDefs1 = getColumnDefs1(props);

  return (
    <Box sx={{ height: '100%', pt: 1 }}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderColor: 'divider',
          borderRadius: '10px',
          p: 2,
          pt: 1,
          bgcolor: '#FFF',
        }}
      >
        <Stack
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          <React.Fragment>
            <Box>
              {/* 상단 타이틀 */}
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pb: 1,
                }}
              >
                <MuiBox sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: '1.05rem',
                      fontWeight: 'bold',
                      color: '#2CA900',
                      mr: 1,
                    }}
                  >
                    계측기
                  </Typography>
                </MuiBox>
              </Box>
            </Box>

            {/* AG Grid */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                width: '100%',
              }}
            >
              {/* 버튼 전체 영역 */}
              <div
                ref={buttonRef}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '4px',
                  margin: '8px 0',
                }}
              >
                {/* 왼쪽: GridFilterButton */}
                <div>
                  {props.gridRef1 && (
                    <GridFilterButton
                      gridRef={props.gridRef1}
                      gridApi={gridApi}
                      gridColumnApi={gridColumnApi}
                      rowData={props.rowData1}
                      selectedFiltersRef={selectedFiltersRef}
                      selectedFilters={selectedFilters}
                      setSelectedFilters={setSelectedFilters}
                      setIsFiltered={setIsFiltered}
                      setHeaderMap={setHeaderMap}
                      headerMap={headerMap}
                    />
                  )}
                </div>

                {/* 오른쪽: 나머지 버튼들 */}
                <div style={{ display: 'flex', gap: '4px' }}>
                  {/* motion.div는 추가 버튼 전용 */}
                  <motion.div
                    style={{ display: 'flex', alignItems: 'center' }}
                    animate={
                      isAddButtonError ? { y: [0, -20, 0, -10, 0] } : { y: 0 }
                    }
                    transition={
                      isAddButtonError
                        ? {
                            duration: 0.6,
                            repeat: Infinity,
                            repeatDelay: 1,
                            ease: 'easeOut',
                          }
                        : { duration: 0 }
                    }
                  >
                    <LargeTooltip title="추가" arrow placement="top">
                      <IconButton
                        color="primary"
                        sx={toolbarIconBtnSx}
                        onClick={() => {
                          if (!props.validateSelection()) return;

                          dispatch(resetAddButtonError());
                          GridFunctions.addClick(
                            props.gridRef1,
                            props.setChangedRowData1
                          );

                          // ✅ 새로 추가된 로우에 기본값 세팅
                          const api = props.gridRef1.current.api;
                          const newRowNode = api.getDisplayedRowAtIndex(0);

                          if (newRowNode) {
                            const today = dayjs();
                            const calibrationDate = new Date(
                              Date.UTC(
                                today.year(),
                                today.month(),
                                today.date()
                              )
                            ).toISOString();

                            newRowNode.setDataValue('measurementCycle', '1년');
                            newRowNode.setDataValue(
                              'calibrationDate',
                              calibrationDate
                            );

                            // 차기 교정일자 자동 계산
                            const nextDate = CalculateNextCalibrationDate(
                              calibrationDate,
                              '1년'
                            );
                            if (nextDate) {
                              newRowNode.setDataValue(
                                'nextCalibrationDate',
                                nextDate
                              );
                            }
                          }
                        }}
                        variant="outlined"
                      >
                        <AddIcon fontSize="small" style={{ margin: 0 }} />
                      </IconButton>
                    </LargeTooltip>
                  </motion.div>

                  {/* 검색 버튼 */}
                  <LargeTooltip title="검색" arrow placement="top">
                    <IconButton
                      color="secondary"
                      sx={toolbarIconBtnSx}
                      onClick={() => {
                        if (!props.validateSelection()) return;
                        props.SearchLogic1();
                      }}
                      variant="outlined"
                    >
                      <RefreshIcon fontSize="small" style={{ margin: 0 }} />
                    </IconButton>
                  </LargeTooltip>

                  {/* 삭제 버튼 */}
                  <LargeTooltip title="삭제" arrow placement="top">
                    <IconButton
                      color="error"
                      sx={toolbarIconBtnSx}
                      variant="outlined"
                      onClick={(e) => {
                        if (!props.validateSelection()) return;

                        const newRowExists = props.selectedRowData1.some(
                          (row) => row.newYn === 'Y'
                        );

                        if (!newRowExists) {
                          props.MarkedForDeletion1(props.selectedRowData1);
                        } else {
                          props.NewYnErrorMsg();
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" style={{ margin: 0 }} />
                    </IconButton>
                  </LargeTooltip>

                  {/* 엑셀 업로드 버튼 */}
                  <LargeTooltip title="엑셀 업로드" arrow placement="top">
                    <IconButton
                      color="info"
                      sx={toolbarIconBtnSx}
                      variant="outlined"
                      onClick={() => {
                        if (!props.validateSelection()) return;
                        props.fileInputRef?.current?.click();
                      }}
                    >
                      <Icon.FolderUpload
                        style={{ fontSize: 'small', margin: 0 }}
                      />
                    </IconButton>
                  </LargeTooltip>

                  {/* 엑셀 다운로드 버튼 */}
                  <LargeTooltip title="엑셀 다운로드" arrow placement="top">
                    <IconButton
                      color="success"
                      sx={toolbarIconBtnSx}
                      variant="outlined"
                      onClick={() => {
                        if (!props.validateSelection()) return;
                        props.ExcelDownload1();
                      }}
                    >
                      <Icon.FolderDownload
                        style={{ fontSize: 'small', margin: 0 }}
                      />
                    </IconButton>
                  </LargeTooltip>
                </div>
              </div>

              {props.gridRef1 && (
                <GridFilterSummary
                  isFiltered={isFiltered}
                  selectedFilters={selectedFilters}
                  headerMap={headerMap}
                  setSelectedFilters={setSelectedFilters}
                />
              )}
              <AgGridReact
                getRowId={(params) => {
                  return params.data.no;
                }}
                ref={props.gridRef1}
                onGridReady={(params) => {
                  setGridApi(params.api);
                  setGridColumnApi(params.columnApi);
                }}
                animationRows={true}
                columnDefs={columnDefs1}
                rowData={props.rowData1}
                setSelectedRowData={props.setSelectedRowData1}
                setChangedRowData={props.setChangedRowData1}
                defaultColDef={{
                  cellDataType: false,
                  minWidth: 100,
                  editable: true,
                  resizable: true,
                  sortable: true,
                  filter: true,
                  floatingFilter: true,
                }}
                gridOptions={gridOptions}
                rowSelection={'multiple'}
                singleClickEdit={true}
                onCellClicked={(e) => {
                  const gridApi = props.gridRef1.current.api;
                  const clickedRowIndex = e.rowIndex;

                  const isCheckboxClick = e.event?.target?.type === 'checkbox';

                  if (!isCheckboxClick) {
                    gridApi.ensureIndexVisible(clickedRowIndex, 'middle');
                    gridApi.setFocusedCell(clickedRowIndex, 'coNm');

                    const copied = JSON.parse(JSON.stringify(e.data));
                    props.handleRowSelection1(copied, clickedRowIndex);

                    gridApi
                      .getDisplayedRowAtIndex(clickedRowIndex)
                      ?.setSelected(true, false);
                  }
                }}
                onSelectionChanged={() => {
                  const gridApi = props.gridRef1?.current?.api;
                  const selectedNodes = gridApi.getSelectedNodes();
                  const selectedData = selectedNodes.map((n) => n.data);

                  props.selectedRowNosRef1.current = new Set(
                    selectedData.map((r) => r.no)
                  );
                  props.setSelectedRowData1(selectedData);
                }}
                onCellValueChanged={(e) => {
                  const key = e.data.no;
                  const field = e.colDef.field;
                  const newValue = e.newValue;

                  const isManualCycle = (v) =>
                    v === '직접 입력' || v === '기타';

                  // 검교정 주기 변경 시 차기 교정일자 자동 계산 (직접 입력·기타 제외)
                  if (field === 'measurementCycle') {
                    const calibrationDate = e.data.calibrationDate;
                    if (
                      newValue &&
                      calibrationDate &&
                      !isManualCycle(newValue)
                    ) {
                      const nextDate = CalculateNextCalibrationDate(
                        calibrationDate,
                        newValue
                      );
                      if (nextDate) {
                        e.node.setDataValue('nextCalibrationDate', nextDate);
                      }
                    } else if (isManualCycle(newValue) && calibrationDate) {
                      e.node.setDataValue(
                        'nextCalibrationDate',
                        calibrationDate
                      );
                    }
                  }

                  // 검교정일자 변경 시 차기 교정일자 자동 계산 (직접 입력·기타 제외)
                  if (field === 'calibrationDate') {
                    const measurementCycle = e.data.measurementCycle;
                    if (newValue && measurementCycle) {
                      if (isManualCycle(measurementCycle)) {
                        e.node.setDataValue('nextCalibrationDate', newValue);
                      } else {
                        const nextDate = CalculateNextCalibrationDate(
                          newValue,
                          measurementCycle
                        );
                        if (nextDate) {
                          e.node.setDataValue('nextCalibrationDate', nextDate);
                        }
                      }
                    }
                  }

                  e.node.setData({
                    ...e.data,
                    editYn: 'Y',
                  });

                  props.setChangedRowData1?.((prev) => {
                    const filtered = prev.filter((row) => row.no !== key);
                    return [...filtered, { ...e.data, editYn: 'Y' }];
                  });
                }}
                stopEditingWhenCellsLoseFocus={true}
              />
            </div>
          </React.Fragment>
        </Stack>
      </Box>

      {/* 그리드안 사진 설정하기 팝업 창 */}
      {/* 파일 업로드 다이얼로그 */}
      {props.fileModalToggle && (
        <FileUploadDialog
          open={props.fileModalToggle}
          onClose={() => {
            props.setFileModalToggle(false);
          }}
          gridRef={props.gridRef1}
          selectedRowData={props.selectedRowData1}
          setSelectedRowData={props.setSelectedRowData1}
          setChangedRowData={props.setChangedRowData1}
          fileNameField="instrumentImageFileName"
          saveNameField="instrumentImageSaveName"
          filePathField="instrumentImageFilePath"
          previewFilePathField="previewFilePath"
          SaveLogicPreview={props.SaveLogicPreview}
          DeleteLogicFile={props.DeleteLogicFile}
          DownloadLogic={props.DownloadLogic1}
        />
      )}
      {/* 엑셀 업로드용 숨김 파일 input */}
      <input
        type="file"
        ref={props.fileInputRef}
        style={{ display: 'none' }}
        accept=".xlsx,.xls"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            props.ExcelUpload1(file);
            // 같은 파일을 다시 선택할 수 있도록 초기화
            e.target.value = '';
          }
        }}
      />
    </Box>
  );
}

export default HAZ_BASIC_03;
